import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

/**
 * A confirmation prompt for an action that cannot be undone.
 *
 * WHY THIS IS A SHARED COMPONENT
 * ------------------------------
 * "Are you sure?" is the same question everywhere, and the ways to get it wrong
 * are the same everywhere too: a destructive action styled as the default, a
 * dialog with no accessible name, a confirm button that does not say what it
 * will do. Writing it once means those mistakes are fixed once.
 *
 * THE RULES IT ENFORCES
 * ---------------------
 *  - The confirm button is `danger` by default. A confirmation exists because
 *    the action is costly; styling it as the safe default invites the misclick
 *    the dialog was added to prevent.
 *  - The confirm button's label is the ACTION, not "OK". "Discard draft" tells
 *    the user what will happen; "OK" makes them re-read the body to find out.
 *  - Cancel is the initial focus, so Enter on a dialog the user did not expect
 *    dismisses rather than destroys.
 *  - The title is the accessible name. A dialog without one is announced as an
 *    unnamed dialog, which is the same as no warning at all.
 *
 * It owns no state: `open` and `onOpenChange` are the caller's, because only the
 * caller knows what is being confirmed.
 */
export type ConfirmDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  /** The confirm button's label. Must name the action, not say "OK". */
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  /** `danger` for destructive actions, `primary` for merely consequential ones. */
  readonly tone?: 'danger' | 'primary';
  readonly busy?: boolean;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  tone = 'danger',
  busy = false,
}: ConfirmDialogProps): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {/*
            Cancel is rendered first so it takes initial focus. A dialog the user
            did not expect should dismiss on Enter, not destroy their work.
          */}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button variant={tone} loading={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
